import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * A chart, said and shown (#565): what it is, what it counts, the picture, and
 * the same figures as a table for whoever needs the number rather than the
 * shape.
 *
 * Every chart in the app sits in one of these, which is what makes a title and
 * a description something a chart cannot be drawn without. The table is closed
 * until asked for: a GST total is filed from the figure, not the bar, and a
 * chart is unreadable to someone who cannot tell its colours apart — so the
 * table is always there, and never in the way.
 */
export function ChartCard({
  title,
  description,
  children,
  table,
  className,
}: {
  title: string;
  /** What the chart counts, over what dates, and what it leaves out. */
  description: ReactNode;
  children: ReactNode;
  /** The chart's figures as a table, behind "View as table". */
  table?: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("gap-4", className)}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {children}
        {table ? (
          <details className="group border-t pt-3">
            <summary className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 inline-flex cursor-pointer list-none items-center gap-1 rounded-sm text-xs font-medium outline-none select-none focus-visible:ring-[3px] [&::-webkit-details-marker]:hidden">
              <ChevronRight
                aria-hidden
                className="size-3.5 transition-transform group-open:rotate-90"
              />
              View as table
            </summary>
            <div className="overflow-x-auto pt-2">{table}</div>
          </details>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** One column of a `FigureTable`. */
export interface FigureColumn<Row> {
  header: string;
  cell: (row: Row, index: number) => ReactNode;
  /** Figures line up on the right; names on the left. */
  numeric?: boolean;
  /** The total, said a little louder than the parts. */
  strong?: boolean;
}

/**
 * A chart's figures as a plain table: the exact numbers behind the picture.
 * One component, so every report's table reads the same way.
 */
export function FigureTable<Row>({
  columns,
  rows,
  rowKey,
}: {
  columns: readonly FigureColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row, index: number) => string;
}) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b text-left">
          {columns.map((column) => (
            <th
              key={column.header}
              scope="col"
              className={cn(
                "text-muted-foreground py-2 pr-3 font-normal whitespace-nowrap last:pr-0",
                column.numeric && "text-right",
              )}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={rowKey(row, index)} className="border-b last:border-b-0">
            {columns.map((column) => (
              <td
                key={column.header}
                className={cn(
                  "py-1.5 pr-3 last:pr-0",
                  column.numeric && "text-right whitespace-nowrap tabular-nums",
                  column.strong && "font-medium",
                )}
              >
                {column.cell(row, index)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
